// Temporary sessionStorage-backed mock for the EXR monitoring list until the EXR backend exists. (GMTEC-20708)
// TODO: Replace whole file with real API calls once the EXR record endpoints exist; delete together with the other EXR mocks. (GMTEC-20708)
import { SpecialPaymentProposalState } from '@coin/shared/core/util-enums';
import { PaginatedResult } from '@coin/shared/core/util-models';
import { ExceptionalRecognitionPaymentCategory } from '@coin/shared/exceptional-recognitions/util';
import { ExceptionalRecognitionRecord } from '../models/exceptional-recognition-record.model';
import { EXR_RECORD_MOCK_DATA } from './exr-record-mock-data';

const RECORDS_KEY = 'exr-mock-records';

// States in which every COIN approval step is done — manual and automatic approval both end here.
const APPROVED_STATES = new Set([SpecialPaymentProposalState.Approved, SpecialPaymentProposalState.AutoApproved]);

// Columns filtered by substring; every other filterable column matches exactly.
const CONTAINS_KEYS = new Set([
  'assignmentId',
  'gid',
  'firstname',
  'lastname',
  'are',
  'orgCode',
  'assignedPlan',
  'currency'
]);
// Yes/No flag columns stored as booleans.
const BOOLEAN_KEYS = new Set(['boardApproval', 'cfoApproval']);

export function listExrRecords(
  seasonId: string,
  page = 0,
  size = 0,
  filterText = '',
  sortText = ''
): PaginatedResult<ExceptionalRecognitionRecord> {
  const records = sortRecords(filterRecords(ensureRecords(seasonId), filterText), sortText);
  const pageSize = size || records.length || 1;
  const start = page * pageSize;
  return {
    content: records.slice(start, start + pageSize),
    total: records.length,
    currentPage: page + 1,
    pageCount: Math.max(1, Math.ceil(records.length / pageSize)),
    pageSize,
    hasMore: start + pageSize < records.length
  };
}

export function getExrRecordFieldValues(
  seasonId: string,
  targetProperty: string,
  page = 0,
  size = 0,
  searchText = ''
): PaginatedResult<string> {
  const search = searchText.trim().toLowerCase();
  const values = [
    ...new Set(
      ensureRecords(seasonId)
        .map(record => `${record[targetProperty as keyof ExceptionalRecognitionRecord] ?? ''}`)
        .filter(Boolean)
    )
  ]
    .filter(value => !search || value.toLowerCase().includes(search))
    .sort((a, b) => a.localeCompare(b));
  const pageSize = size || values.length || 1;
  const start = page * pageSize;
  return {
    content: values.slice(start, start + pageSize),
    total: values.length,
    currentPage: page + 1,
    pageCount: Math.max(1, Math.ceil(values.length / pageSize)),
    pageSize,
    hasMore: start + pageSize < values.length
  };
}

// Resolves individual records for the mock endpoints that act on a single proposal rather than on a selection. (GMTEC-20707)
export function getExrRecordsByIds(seasonId: string, recordIds: string[]): ExceptionalRecognitionRecord[] {
  const ids = new Set(recordIds);
  return ensureRecords(seasonId).filter(record => ids.has(record.id));
}

export interface ExrMassApprovalOutcome {
  modifiedIds: string[];
  skipped: { recordId: string; failureReason: string }[];
}

// Applies a Board/CFO mass-approval to the selected EXR records (by id, or by the current filter when select-all is
// used), setting the flag on eligible Transformation Incentive proposals and reporting the rest as skipped. (GMTEC-20710)
export function applyExrMassApproval(
  seasonId: string,
  selection: { recordIds?: string[]; filterText?: string },
  flag: 'board' | 'cfo',
  cfoThresholdAmount: number
): ExrMassApprovalOutcome {
  const records = ensureRecords(seasonId);
  const targets = selectRecords(records, selection);

  const outcome: ExrMassApprovalOutcome = { modifiedIds: [], skipped: [] };
  for (const record of targets) {
    const skipReason = getApprovalSkipReason(record, flag, cfoThresholdAmount);
    if (skipReason) {
      outcome.skipped.push({ recordId: record.id, failureReason: skipReason });
      continue;
    }
    if (flag === 'board') {
      record.boardApproval = true;
    } else {
      record.cfoApproval = true;
    }
    outcome.modifiedIds.push(record.id);
  }
  writeRecords(seasonId, records);
  return outcome;
}

export interface ExrMassDeleteOutcome {
  deletedIds: string[];
  failed: { recordId: string; failureReason: string }[];
}

// Deletes the selected EXR records (by id, or by the current filter when select-all is used). Fully approved proposals
// are protected and reported back as failures. (GMTEC-20711)
export function deleteExrRecords(
  seasonId: string,
  selection: { recordIds?: string[]; filterText?: string },
  cfoThresholdAmount: number
): ExrMassDeleteOutcome {
  const records = ensureRecords(seasonId);
  const targets = selectRecords(records, selection);

  const outcome: ExrMassDeleteOutcome = { deletedIds: [], failed: [] };
  for (const record of targets) {
    if (isFullyApproved(record, cfoThresholdAmount)) {
      outcome.failed.push({ recordId: record.id, failureReason: 'Fully approved proposals cannot be deleted' });
      continue;
    }
    outcome.deletedIds.push(record.id);
  }

  // The real backend cascades to the proposal's related entities via EF (special payment cascades approvals, letters,
  // warnings and AI validation off the record); the mock only holds the record itself, so dropping it is the whole deletion.
  const deletedIds = new Set(outcome.deletedIds);
  writeRecords(
    seasonId,
    records.filter(record => !deletedIds.has(record.id))
  );
  return outcome;
}

export interface ExrPayrollExportOutcome {
  exportedIds: string[];
  blocked: { recordId: string; failureReason: string }[];
  // Targets that are simply outside the final export set (already exported, or not approved yet). Counted rather than
  // listed per record, so the summary total still reconciles with the selection without one warning per record.
  skippedCount: number;
  downloadUri: string;
  fileName: string;
}

// Builds a payroll export for the selected EXR records (by id, or by the current filter when select-all is used).
// A test export includes every matching record and changes nothing. A final export only takes fully approved,
// not-yet-exported records (Sign-On Bonus additionally needs a mapped employee), flips them to ExportState = Done and
// reports the approved-but-blocked ones (missing Board/CFO flag, unmapped Sign-On Bonus) as warnings. (GMTEC-20709)
export function exportExrRecords(
  seasonId: string,
  selection: { recordIds?: string[]; filterText?: string },
  mode: 'test' | 'final',
  cfoThresholdAmount: number
): ExrPayrollExportOutcome {
  const records = ensureRecords(seasonId);
  const targets = selectRecords(records, selection);

  if (mode === 'test') {
    return {
      exportedIds: targets.map(record => record.id),
      blocked: [],
      skippedCount: 0,
      ...buildExportFile(targets, mode)
    };
  }

  const exported: ExceptionalRecognitionRecord[] = [];
  const blocked: { recordId: string; failureReason: string }[] = [];
  let skippedCount = 0;
  for (const record of targets) {
    // Already exported or not yet approved: outside the final export set, so counted as skipped instead of blocked.
    if (record.payrollExport === 'Done' || !APPROVED_STATES.has(record.state)) {
      skippedCount++;
      continue;
    }
    const blockReason = getExportBlockReason(record, cfoThresholdAmount);
    if (blockReason) {
      blocked.push({ recordId: record.id, failureReason: blockReason });
      continue;
    }
    record.payrollExport = 'Done';
    exported.push(record);
  }
  writeRecords(seasonId, records);
  return {
    exportedIds: exported.map(record => record.id),
    blocked,
    skippedCount,
    ...buildExportFile(exported, mode)
  };
}

// For an approved, not-yet-exported record, returns why it still can't be part of a final export, or null when it is
// eligible. Transformation Incentive needs the Board flag and, from the CFO threshold upwards, the CFO flag; a
// Sign-On Bonus needs a mapped employee (a GID). (GMTEC-20709)
function getExportBlockReason(record: ExceptionalRecognitionRecord, cfoThresholdAmount: number): string | null {
  if (record.category === ExceptionalRecognitionPaymentCategory.SignOnBonus) {
    return record.gid ? null : 'Sign-On Bonus without a mapped employee';
  }
  if (record.category === ExceptionalRecognitionPaymentCategory.TransformationIncentive) {
    if (!record.boardApproval) {
      return 'Missing Board approval';
    }
    if (record.amount >= cfoThresholdAmount && !record.cfoApproval) {
      return 'Missing CFO approval';
    }
  }
  return null;
}

// The ticket's full export field list is produced by the real backend (XLSX, or CSV above 60k rows); the mock only
// holds a subset of those columns, so it renders the available ones as a downloadable CSV. (GMTEC-20709)
// TODO: Replace with the real payroll export file once the EXR export endpoint exists. (GMTEC-20709)
function buildExportFile(
  records: ExceptionalRecognitionRecord[],
  mode: 'test' | 'final'
): { downloadUri: string; fileName: string } {
  const columns: { header: string; value: (record: ExceptionalRecognitionRecord) => string }[] = [
    { header: 'GID', value: record => record.gid },
    { header: 'Firstname', value: record => record.firstname },
    { header: 'Lastname', value: record => record.lastname },
    { header: 'OrgCode', value: record => record.orgCode },
    { header: 'ARE', value: record => record.are },
    { header: 'Place of Action', value: record => record.placeOfAction },
    { header: 'Assigned Plan', value: record => record.assignedPlan },
    { header: 'Category', value: record => record.category },
    { header: 'Compensation Type', value: record => record.compensationType },
    { header: 'Proposal Date', value: record => record.proposalDate },
    { header: 'Award Amount in EUR', value: record => `${record.amount}` },
    { header: 'Local Currency', value: record => record.currency },
    { header: 'Current Status', value: record => record.state }
  ];
  const escapeCsv = (value: string): string => (/[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
  const rows = [
    columns.map(column => column.header),
    ...records.map(record => columns.map(column => escapeCsv(column.value(record))))
  ];
  // Prefix a UTF-8 BOM so Excel opens the CSV with the correct encoding.
  const csv = `${String.fromCharCode(0xfeff)}${rows.map(row => row.join(',')).join('\n')}`;
  const fileName = `exceptional-recognition-payroll-${mode}-export-${new Date().toISOString().slice(0, 10)}.csv`;
  return { downloadUri: `data:text/csv;charset=UTF-8,${encodeURIComponent(csv)}`, fileName };
}

// A proposal is fully approved once every COIN approval step is done. Transformation Incentive additionally requires the
// Board flag and — from the season's CFO threshold upwards — the CFO flag. (GMTEC-20711)
function isFullyApproved(record: ExceptionalRecognitionRecord, cfoThresholdAmount: number): boolean {
  if (!APPROVED_STATES.has(record.state)) {
    return false;
  }
  if (record.category !== ExceptionalRecognitionPaymentCategory.TransformationIncentive) {
    return true;
  }
  const needsCfoApproval = record.amount >= cfoThresholdAmount;
  return !!record.boardApproval && (!needsCfoApproval || !!record.cfoApproval);
}

// Manual selection targets the given ids; select-all targets everything matching the active filter.
function selectRecords(
  records: ExceptionalRecognitionRecord[],
  selection: { recordIds?: string[]; filterText?: string }
): ExceptionalRecognitionRecord[] {
  if (!selection.recordIds?.length) {
    return filterRecords(records, selection.filterText ?? '');
  }
  const idSet = new Set(selection.recordIds);
  return records.filter(record => idSet.has(record.id));
}

// Returns why a record can't receive the flag, or null when it is eligible.
function getApprovalSkipReason(
  record: ExceptionalRecognitionRecord,
  flag: 'board' | 'cfo',
  cfoThresholdAmount: number
): string | null {
  if (record.category !== ExceptionalRecognitionPaymentCategory.TransformationIncentive) {
    return 'Not a Transformation Incentive proposal';
  }
  const alreadyApproved = flag === 'board' ? record.boardApproval : record.cfoApproval;
  if (alreadyApproved) {
    return flag === 'board' ? 'Board approval already set' : 'CFO approval already set';
  }
  if (flag === 'cfo' && record.amount < cfoThresholdAmount) {
    return 'Amount below the CFO approval threshold';
  }
  return null;
}

function filterRecords(records: ExceptionalRecognitionRecord[], filterText: string): ExceptionalRecognitionRecord[] {
  const params = new URLSearchParams(filterText.replace(/^[&?]/, ''));
  const filters = [...new Set(params.keys())].map(rawKey => ({
    key: rawKey.replace(/^Query\./, ''),
    values: params.getAll(rawKey)
  }));

  return records.filter(record =>
    filters.every(({ key, values }) => values.some(value => matches(record, key, value)))
  );
}

function matches(record: ExceptionalRecognitionRecord, key: string, value: string): boolean {
  const recordValue = record[key as keyof ExceptionalRecognitionRecord];
  if (BOOLEAN_KEYS.has(key)) {
    return recordValue === (value === 'Yes');
  }
  if (CONTAINS_KEYS.has(key)) {
    return `${recordValue ?? ''}`.toLowerCase().includes(value.toLowerCase());
  }
  return `${recordValue ?? ''}` === value;
}

function sortRecords(records: ExceptionalRecognitionRecord[], sortText: string): ExceptionalRecognitionRecord[] {
  const params = new URLSearchParams(sortText.replace(/^[&?]/, ''));
  const property = params.get('Sorting.Property');
  if (!property) {
    return records;
  }
  const direction = params.get('Sorting.OrderBy') === 'desc' ? -1 : 1;
  return [...records].sort(
    (a, b) =>
      `${a[property as keyof ExceptionalRecognitionRecord] ?? ''}`.localeCompare(
        `${b[property as keyof ExceptionalRecognitionRecord] ?? ''}`
      ) * direction
  );
}

function ensureRecords(seasonId: string): ExceptionalRecognitionRecord[] {
  const store = readStore();
  if (!store[seasonId]) {
    store[seasonId] = structuredClone(EXR_RECORD_MOCK_DATA);
    sessionStorage.setItem(RECORDS_KEY, JSON.stringify(store));
  }
  return store[seasonId];
}

function readStore(): Record<string, ExceptionalRecognitionRecord[]> {
  try {
    return JSON.parse(sessionStorage.getItem(RECORDS_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeRecords(seasonId: string, records: ExceptionalRecognitionRecord[]): void {
  const store = readStore();
  store[seasonId] = records;
  sessionStorage.setItem(RECORDS_KEY, JSON.stringify(store));
}
